import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
  useResetDashboardLayoutMutation,
  useSaveDashboardLayoutMutation,
} from "@/services/(company)/api.dashboard-layout";
import type { TDashboardScope } from "@/types/(company)/dashboardLayout.interface";
import {
  LAYOUT_SAVED_FLASH_MS,
  LAYOUT_SAVE_DEBOUNCE_MS,
  LAYOUT_VERSION,
} from "./grid.constants";
import type { IWidgetPlacement, TLayoutSaveState } from "./types";

interface IUseLayoutPersistenceArgs {
  scope: TDashboardScope;
  /** The current arrangement. Null before hydration. */
  placements: IWidgetPlacement[] | null;
  /** True when `placements` differs from what the server has. */
  isDirty: boolean;
  /** Called with the exact arrangement the server accepted. */
  onPersisted: (placements: IWidgetPlacement[]) => void;
}

/**
 * The canvas' one seam with the server.
 *
 * Everything above this hook treats the layout as local state; everything below
 * it is a write. It watches for the state going dirty and debounces a write,
 * rather than every edit remembering to schedule one — which is why adding a new
 * kind of edit to the reducer needs no change here at all.
 *
 * The debounce can stay short because the two moments that actually matter —
 * leaving customize mode, and unmounting — flush synchronously. So a drag never
 * waits on a round trip, and navigating away mid-edit still persists.
 */
export function useLayoutPersistence({
  scope,
  placements,
  isDirty,
  onPersisted,
}: IUseLayoutPersistenceArgs) {
  const { t } = useTranslation(["common"]);
  const [saveLayout] = useSaveDashboardLayoutMutation();
  const [resetLayout] = useResetDashboardLayoutMutation();
  const [saveState, setSaveState] = useState<TLayoutSaveState>("idle");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The newest arrangement and whether it still needs writing.
   *
   * `flush` reads this rather than a queue the in-flight write has already
   * drained. That distinction is the whole bug it fixes: a failed save used to
   * leave nothing for Done or unmount to retry, so the canvas sat on "Not saved"
   * and the next flush was a no-op.
   */
  const latestRef = useRef<{
    placements: IWidgetPlacement[] | null;
    isDirty: boolean;
  }>({ placements, isDirty });

  useEffect(() => {
    latestRef.current = { placements, isDirty };
  }, [placements, isDirty]);

  const onPersistedRef = useRef(onPersisted);
  useEffect(() => {
    onPersistedRef.current = onPersisted;
  }, [onPersisted, t]);

  const clearTimer = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
  }, []);

  /** Guards against a flush firing a second write while one is already going. */
  const inFlightRef = useRef(false);

  const commit = useCallback(
    async (next: IWidgetPlacement[]) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setSaveState("pending");
      try {
        await saveLayout({
          scope,
          version: LAYOUT_VERSION,
          widgets: next,
        }).unwrap();

        onPersistedRef.current(next);
        setSaveState("saved");
        if (flashRef.current) clearTimeout(flashRef.current);
        flashRef.current = setTimeout(
          () => setSaveState("idle"),
          LAYOUT_SAVED_FLASH_MS,
        );
      } catch {
        // `isDirty` stays true, so the arrangement is still queued in
        // `latestRef` and Done, unmount or Retry can all try again.
        setSaveState("error");
        toast.error(t("common:dashboard_grid.could_not_save_layout"));
      } finally {
        inFlightRef.current = false;
      }
    },
    [saveLayout, scope, t],
  );

  // Dirty state is the trigger. One effect, rather than a `schedule()` call at
  // every edit site, so no edit can forget to persist itself.
  useEffect(() => {
    if (!isDirty || !placements) return;

    clearTimer();
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void commit(placements);
    }, LAYOUT_SAVE_DEBOUNCE_MS);

    return clearTimer;
  }, [isDirty, placements, commit, clearTimer]);

  /**
   * Write the current arrangement now instead of waiting out the debounce.
   *
   * Also the retry path: it asks "is there anything unsaved" rather than "is
   * there anything queued", so it works just as well after a failed write as
   * after a pending one.
   */
  const flush = useCallback(() => {
    clearTimer();
    const { placements: latest, isDirty: stillDirty } = latestRef.current;
    if (stillDirty && latest) void commit(latest);
  }, [clearTimer, commit]);

  /** Drop the saved row. */
  const resetRemote = useCallback(async (): Promise<boolean> => {
    clearTimer();
    setSaveState("pending");
    try {
      await resetLayout({ scope }).unwrap();
      setSaveState("idle");
      return true;
    } catch {
      setSaveState("error");
      toast.error(t("common:dashboard_grid.could_not_reset_layout"));
      return false;
    }
  }, [clearTimer, resetLayout, scope, t]);

  // Flush on unmount. Held in a ref an effect keeps current so the cleanup can
  // stay mounted-once and still call the latest closure.
  const flushRef = useRef(flush);
  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);
  useEffect(
    () => () => {
      flushRef.current();
      if (flashRef.current) clearTimeout(flashRef.current);
    },
    [],
  );

  return { saveState, flush, resetRemote };
}
